import { NgFor, NgIf } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

import { Store } from '@ngrx/store';

import { UploadService } from '@services/upload.service';

import { selectIsOrdersManager } from '../../../../@store/user/user.selectors';

@Component({
  selector: 'ciwc-order-attachments',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    NgIf,
    NgFor,
    MatProgressSpinner,
  ],
  standalone: true,
  templateUrl: './order-attachments.component.html',
  styleUrl: './order-attachments.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderAttachmentsComponent implements AfterViewInit {
  private readonly store = inject(Store);
  readonly isOrdersManager = this.store.selectSignal(selectIsOrdersManager);

  @Input() attachments!: FormArray;
  @Input() parentForm!: FormGroup;
  @Input() viewDisable!: boolean;

  private uploadService = inject(UploadService);

  constructor(private fb: FormBuilder) {}

  ngAfterViewInit() {
    if (this.viewDisable && this.attachments) {
      this.attachments.controls.forEach((control) => {
        control.get('name')?.disable();
        control.get('document')?.disable();
      });
    }
  }

  addAttachment() {
    const newAttachment = this.fb.group({
      name: [''],
      document: [''],
      url: [''],
      isUploading: [false],
      uploadError: [''],
    });

    if (this.viewDisable) {
      newAttachment.get('name')?.disable();
      newAttachment.get('document')?.disable();
    }

    this.attachments.push(newAttachment);
  }

  removeAttachment(index: number) {
    this.attachments.removeAt(index);
  }

  triggerFileInput(index: number) {
    const fileInput = document.getElementById('fileInput' + index) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  onFileSelected(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const attachmentGroup = this.attachments.at(index);
      attachmentGroup.patchValue({
        isUploading: true,
        uploadError: '',
      });

      this.uploadService.uploadFile(file).subscribe({
        next: (res) => {
          attachmentGroup.patchValue({
            document: res.name,
            url: res.url,
            isUploading: false,
            uploadError: '',
          });
        },
        error: (err) => {
          attachmentGroup.patchValue({
            isUploading: false,
            uploadError: 'Failed to upload file. Please try again.',
          });
        },
      });
    }
  }

  viewAttachment(index: number) {
    const attachment = this.attachments.at(index).value;
    if (attachment.url) {
      window.open(attachment.url, '_blank');
      return;
    }
    const file = attachment.document;
    if (!file) return;
    if (file instanceof File) {
      const blobUrl = URL.createObjectURL(file);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } else if (typeof file === 'string') {
      window.open(file, '_blank');
    }
  }
}
